/**
 * SiteStory — Layer 2 of the restructured analysis panel.
 *
 * Three columns of factual bullets generated from existing enrichment
 * fields. Replaces "47 raw data points" with a synthesis the analyst
 * can scan in 20-30 seconds:
 *
 *   KEY STRENGTHS         MATERIAL CONCERNS      DATA GAPS
 *   Power & Grid          Site Formation          Road Access
 *   420 kV · 0.8 km       45% slope · 20.3 ha    Vegvesen pending
 *
 * Audit 2026-05-27 (professional redesign): emojis and conversational
 * framing ("Excellent X", "Weak Y") removed. Titles are now the
 * factor name; the body line carries the data.
 *
 * Rules:
 *   - STRENGTHS: themes with score_0_1 >= 0.7 (real positives)
 *   - CONCERNS: backend concerns[] + themes with score < 0.4
 *   - DATA GAPS: defaulted themes only — never "Not checked" / "—"
 *   - Each bullet: factor name + concrete value, no encouragement
 *   - Insight lines compute live from existing fields
 */

import React from 'react';
import { siteZone } from '../utils/projectCalcs';

const THEME_LABELS = {
  power:          'Power & Grid',
  planning:       'Planning & Zoning',
  water:          'Water Access',
  site_formation: 'Site Formation',
  access:         'Road Access',
  fiber:          'Network Connectivity',
  environment:    'Environmental Risk',
  permitting:     'Permitting Outlook',
  // ammonia_v1 index themes
  grid:           'Grid Connection',
  port:           'Port & Export',
  power_price:    'Power Price',
  land:           'Land & Platform',
  setback:        'Setback & Protected Areas',
  expansion:      'Expansion Potential',
};

// One-line factual descriptors keyed by theme.
function strengthFor(themeKey, site) {
  switch (themeKey) {
    case 'power': {
      const v = site.nearest_grid_voltage_kv || site.voltage_level_kv;
      const d = site.nearest_grid_distance_km;
      if (d != null && v != null) return `${v} kV transmission · ${d.toFixed(1)} km`;
      return 'High-voltage grid in proximity';
    }
    case 'fiber': {
      const name = site.nearest_ixp_name;
      const km   = site.nearest_ixp_distance_km;
      const fac  = site.facilities_within_50km;
      if (km != null && name) {
        const facBit = fac > 1 ? ` · ${fac} fibre facilities within 50 km` : '';
        return `${name} · ${km.toFixed(0)} km${facBit}`;
      }
      return 'Fibre meet point within range';
    }
    case 'site_formation': {
      const slope = site.slope_pct;
      const buildable = site.capacity_effective_buildable_ha || site.buildable_contiguous_ha;
      if (slope != null && buildable != null) {
        return `${slope.toFixed(0)}% slope · ${buildable.toFixed(1)} ha net buildable`;
      }
      return 'Flat, low-grading-effort terrain';
    }
    case 'water': {
      const km = site.water_distance_km ?? site.nearest_water_distance_km;
      const t  = site.water_source_type || site.nearest_water_type;
      if (km != null) return `${t || 'Water source'} · ${km.toFixed(1)} km`;
      return 'Adequate water proximity';
    }
    case 'access': {
      const km = site.nearest_road_distance_km;
      const cat = site.road_class_label || site.road_category;
      if (km != null) return `${cat || 'Road'} · ${km.toFixed(1)} km`;
      return 'Adequate road access';
    }
    case 'environment':
      return 'No environmental constraints identified within enrichment scope';
    case 'planning': {
      const z = site.zone_name || site.ar5_name;
      return z ? `Zone: ${z}` : 'Compatible zoning';
    }
    case 'expansion': {
      const a = site.total_area_ha || site.areal_ha;
      if (a != null) return `${a.toFixed(0)} ha parcel · room for phased expansion`;
      return 'Sizeable parcel';
    }
    default:
      return null;
  }
}

function concernFor(themeKey, site) {
  switch (themeKey) {
    case 'power': {
      const d = site.nearest_grid_distance_km;
      const u = site.voltage_upgrade_mnok;
      const parts = [];
      if (d != null && d > 5) parts.push(`grid ${d.toFixed(1)} km away`);
      if (u != null && u > 0) parts.push(`voltage upgrade est. ${Math.round(u)} MNOK`);
      if (parts.length === 0) parts.push('Weak grid connection profile');
      return parts.join(' · ');
    }
    case 'fiber': {
      const km = site.nearest_ixp_distance_km;
      if (km != null) {
        const installLow  = Math.round(km * 60);
        const installHigh = Math.round(km * 120);
        return `${km.toFixed(0)} km to nearest IXP · fibre install est. ${installLow}–${installHigh} kNOK + annual lease`;
      }
      return 'Fibre proximity not measured';
    }
    case 'site_formation': {
      const slope = site.slope_pct;
      const maxSlope = site.slope_max_pct;
      const net = site.capacity_effective_buildable_ha;
      const parcel = site.total_area_ha || site.areal_ha;
      const parts = [];
      if (slope != null) {
        if (maxSlope != null && maxSlope > slope + 5) {
          parts.push(`${slope.toFixed(0)}% avg, max ${Math.round(maxSlope)}%`);
        } else {
          parts.push(`${slope.toFixed(0)}% slope`);
        }
      }
      if (net != null && parcel != null) {
        const pct = Math.round((net / parcel) * 100);
        parts.push(`net buildable ${net.toFixed(1)} ha (${pct}% of parcel)`);
      }
      return parts.join(' · ') || 'Significant terrain variation';
    }
    case 'water': {
      const km = site.water_distance_km ?? site.nearest_water_distance_km;
      if (km != null) {
        const cost = Math.round(km * 3.5);
        return `${km.toFixed(1)} km to water source · pipe est. ${cost} MNOK`;
      }
      return 'Water access not measured';
    }
    case 'access': {
      const km = site.nearest_road_distance_km;
      if (km != null) {
        const cost = Math.round(km * 20);
        return `${km.toFixed(1)} km off road network · access build est. ${cost} MNOK`;
      }
      return 'Road access uncertain';
    }
    case 'environment':
      return 'Multiple soft environmental flags — case-by-case permitting';
    case 'planning':
      return 'Land-use plan may require rezoning — consult kommune';
    case 'expansion': {
      const a = site.total_area_ha || site.areal_ha;
      if (a != null) return `${a.toFixed(0)} ha parcel · limited future-phase room`;
      return 'Small parcel · limited expansion';
    }
    case 'permitting':
      return 'Approval pathway not yet measured';
    default:
      return 'Theme below threshold';
  }
}

function actionFor(themeKey) {
  switch (themeKey) {
    case 'access':     return 'Check Statens Vegvesen for site access';
    case 'water':      return 'Run NVE catchment / extraction check';
    case 'permitting': return 'Run Licence Assessment';
    case 'environment':return 'Re-run species and NiN check';
    case 'planning':   return 'Verify with kommune planning office';
    case 'fiber':      return 'Refresh PeeringDB lookup';
    case 'power':      return 'Re-run grid feasibility and queue';
    case 'expansion':  return 'Inspect adjacent parcels manually';
    default:           return 'Re-run enrichment';
  }
}

/**
 * Insight bullets derived from absolute values + project MW.
 * Returns strength/concern entries with factual titles and arithmetic
 * shown so a reviewer can sanity-check.
 */
function deriveInsightBullets(site, useCase, projectMw) {
  const mw = Number(projectMw || 0);
  const utilization = useCase === 'data_center' ? 0.85 : 0.60;
  const hoursYr = 8760;
  const out = { strengths: [], concerns: [] };

  const elecNokMwh = site.electricity_price_nok_mwh;
  if (mw > 0 && elecNokMwh != null) {
    const annualMnok = (mw * utilization * hoursYr * elecNokMwh) / 1_000_000;
    const ore = Math.round(elecNokMwh / 10);           // threshold logic below stays in øre
    const nokMwh = Math.round(elecNokMwh);
    const zone = siteZone(site);
    const usedLive = (site.electricity_price_source || '').startsWith('NordPool');
    const priceLine = usedLive
      ? `${nokMwh} NOK/MWh (30-day spot avg${zone ? `, ${zone}` : ''})`
      : `${nokMwh} NOK/MWh (PPA estimate${zone ? `, ${zone}` : ''})`;
    const costLine = `est. ${annualMnok.toFixed(0)} MNOK/yr at ${mw} MW · ${(utilization*100).toFixed(0)}% utilisation`;
    if (ore <= 50) {
      out.strengths.push({
        key: 'economics_power_price',
        title: 'Power Cost',
        body: `${priceLine} · ${costLine}`,
      });
    } else if (ore >= 80) {
      out.concerns.push({
        key: 'economics_power_price',
        title: 'Power Cost',
        body: `${priceLine} · ${costLine}`,
        severity: 'medium',
      });
    }
  }

  // CO₂ feedstock (hydrogen / ammonia): nearest registered point source.
  if (useCase !== 'data_center' && site.nearest_co2_source) {
    const c = site.nearest_co2_source;
    const body = `${c.name} ${c.distance_km != null ? `${c.distance_km} km away` : ''}${c.co2_kt ? ` · ${c.co2_kt} kt/yr` : ''}${c.sector ? ` · ${c.sector}` : ''} (CO₂ feedstock)`;
    if (c.distance_km != null && c.distance_km <= 50) {
      out.strengths.push({ key: 'co2', title: 'CO₂ Feedstock', body });
    } else {
      out.concerns.push({ key: 'co2', title: 'CO₂ Feedstock', body: `${body} — beyond practical pipeline / trucking range`, severity: 'medium' });
    }
  }

  if (useCase === 'data_center') {
    const days = site.free_cooling_days_yr;
    if (mw > 0 && days != null && elecNokMwh != null) {
      const freeFrac = Math.min(1, days / 365);
      const pueDeltaCold = 0.30 * freeFrac;
      const savingsMnok = (mw * pueDeltaCold * hoursYr * elecNokMwh) / 1_000_000;
      if (days >= 200) {
        out.strengths.push({
          key: 'economics_free_cooling',
          title: 'Free Cooling',
          body: `${days} days/yr · saves est. ${savingsMnok.toFixed(0)} MNOK/yr vs mechanical-only · PUE ≈ ${(1.5 - pueDeltaCold).toFixed(2)}`,
        });
      } else if (days < 100) {
        out.concerns.push({
          key: 'economics_free_cooling',
          title: 'Free Cooling',
          body: `Only ${days} days/yr · cooling overhead drag relative to cold-climate sites`,
          severity: 'medium',
        });
      }
    }
  }

  if (useCase === 'data_center') {
    const km = site.nearest_ixp_distance_km;
    if (km != null && km > 50) {
      const leaseLow  = Math.round(km * 4);
      const leaseHigh = Math.round(km * 8);
      out.concerns.push({
        key: 'economics_fibre_lease',
        title: 'Fibre Lease',
        body: `${km.toFixed(0)} km to nearest IXP · dark fibre lease est. ${leaseLow}–${leaseHigh} kNOK/yr`,
        severity: 'medium',
      });
    }
  }

  return out;
}

export default function SiteStory({ site, useCase, projectMw, onLocate = null }) {
  if (!site) return null;
  const themes = site.readiness_themes || {};
  const concerns = Array.isArray(site.concerns) ? site.concerns : [];

  const strengths = [];
  const concernRows = [];
  const dataGaps = [];

  const insights = deriveInsightBullets(site, useCase, projectMw);
  for (const s of insights.strengths) strengths.push(s);
  for (const c of insights.concerns)  concernRows.push(c);

  for (const [key, t] of Object.entries(themes)) {
    const score01 = Number(t?.score_0_1 ?? 0);
    const defaulted = Boolean(t?.defaulted);
    const title = THEME_LABELS[key] || key;

    if (defaulted) {
      // 2026-06-08 bug fix: backend was marking water + road themes as
      // `defaulted: true` even when the underlying enrichment fields
      // were populated (water_distance_km / sea_name / nearest_road_*).
      // Cross-check the real data before stamping "not measured" —
      // if the value is present we treat it as a strength below
      // instead of leaking a false data-gap row.
      const hasRealData = (
        (key === 'water' && (
          site.water_distance_km != null
          || site.nearest_water_distance_km != null
          || site.sea_name
          || site.river_name
          || site.lake_name
        ))
        || (key === 'access' && (
          site.nearest_road_distance_km != null
          || site.nearest_road_distance_m != null
        ))
      );
      if (!hasRealData) {
        dataGaps.push({
          key, title: `${title} not measured`,
          body: 'No real data from upstream feed',
          action: actionFor(key),
        });
        continue;
      }
      // Fall through to the strength/concern branch so the value
      // actually surfaces.
    }
    if (score01 >= 0.70) {
      const desc = strengthFor(key, site);
      if (desc) strengths.push({ key, title, body: desc });
    } else if (score01 < 0.40) {
      const desc = concernFor(key, site);
      concernRows.push({ key, title, body: desc, severity: 'medium' });
    }
  }

  for (const c of concerns) {
    concernRows.push({
      key: c.type,
      title: c.message.split(' — ')[0],
      body: c.message.includes(' — ') ? c.message.split(' — ').slice(1).join(' — ') : '',
      severity: c.severity,
    });
  }

  const topStrengths = strengths.slice(0, 5);
  const topConcerns  = concernRows
    .sort((a, b) => (b.severity === 'high' ? 1 : 0) - (a.severity === 'high' ? 1 : 0))
    .slice(0, 5);
  const topGaps      = dataGaps.slice(0, 5);

  const noContent = topStrengths.length === 0 && topConcerns.length === 0 && topGaps.length === 0;
  if (noContent) return null;

  // Story items that name a map feature become clickable (dashed line on
  // the map). Water → the measured source (lake / river; the sea has no
  // single point); power / grid → the substation; port → the harbour.
  const locateFor = (key) => {
    if (typeof onLocate !== 'function') return null;
    if (key === 'water') {
      if (site.river_name) return () => onLocate('river', site.river_name, `${site.river_name} (river)`);
      if (site.lake_name)  return () => onLocate('lake',  site.lake_name,  `${site.lake_name} (lake)`);
      return null;
    }
    if (key === 'power' || key === 'grid') {
      const n = site.nearest_grid_name || site.nearest_grid?.name;
      return n ? () => onLocate('substation', n, n) : null;
    }
    if (key === 'port') {
      const n = site.nearest_port_name || site.port_name;
      return n ? () => onLocate('port', n, `${n} (port)`) : null;
    }
    if (key === 'co2') {
      const c = site.nearest_co2_source;
      return (c && c.lat != null) ? () => onLocate('co2', c.name, `${c.name} (CO₂ feedstock)`, { lat: c.lat, lon: c.lon }) : null;
    }
    return null;
  };

  const Column = ({ title, dotClass, items, emptyLine }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="section-header" style={{ marginBottom: 8 }}>
        {title}
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, opacity: 0.5 }}>{emptyLine}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(it => {
            const go = locateFor(it.key);
            return (
            <div key={it.key} style={{ fontSize: 12, cursor: go ? 'pointer' : undefined }}
                 onClick={go || undefined} title={go ? 'Show on map' : undefined}
                 className={go ? 'story-item-locatable' : undefined}>
              <div style={{ fontWeight: 600 }}>
                <span className={`status-dot ${dotClass}`} />
                {it.title}{go && <span style={{ color: '#60a5fa', marginLeft: 6, fontWeight: 400 }} aria-hidden="true">⌖</span>}
              </div>
              {it.body && <div style={{ opacity: 0.75, marginTop: 2, paddingLeft: 12 }}>{it.body}</div>}
              {it.action && (
                <div style={{ opacity: 0.6, marginTop: 2, paddingLeft: 12, fontStyle: 'italic' }}>
                  Action: {it.action}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="analysis-section">
      <div className="analysis-section-header">Site Story</div>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18,
      }}>
        <Column title="Key Strengths"     dotClass="status-strong"   items={topStrengths}
                emptyLine="No themes scored above 70%." />
        <Column title="Material Concerns" dotClass="status-concern"  items={topConcerns}
                emptyLine="No concerns flagged." />
        <Column title="Data Gaps"         dotClass="status-unknown"  items={topGaps}
                emptyLine="All themes measured." />
      </div>
    </div>
  );
}
